import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ScrollToTopOnNavigate } from './components/ScrollToTopOnNavigate';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { AccountDetailPage } from './pages/AccountDetailPage';
import { AccountsPage } from './pages/AccountsPage';
import { ContactsPage } from './pages/ContactsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { TasksPage } from './pages/TasksPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { CataloguePage } from './pages/CataloguePage';
import { DataGovSgPage } from './pages/DataGovSgPage';
import { TicketsPage } from './pages/TicketsPage';
import { NotAuthorizedPage } from './pages/NotAuthorizedPage';
import { SettingsPage } from './pages/SettingsPage';
import { ApprovalsPage } from './pages/ApprovalsPage';
import { PortalChoosePage } from './pages/PortalChoosePage';
import { DemPortalApp } from './components/DemPortalApp';
import { ReportPortalApp } from './components/ReportPortalApp';
import { PurchasingPortalApp } from './components/PurchasingPortalApp';
import { DesignPortalApp } from './components/DesignPortalApp';
import { PortalApp } from './components/PortalApp';
import { DemCustomersPage } from './pages/dem/DemCustomersPage';
import { DemItemsPage } from './pages/dem/DemItemsPage';
import { DemSkuMapPage } from './pages/dem/DemSkuMapPage';
import { DemStandardSalesLinesPage } from './pages/dem/DemStandardSalesLinesPage';
import { DemCloudTalkPage } from './pages/dem/DemCloudTalkPage';
import { DemAnalyticsPage } from './pages/dem/DemAnalyticsPage';
import { DemThemePreviewPage } from './pages/dem/DemThemePreviewPage';
import { DemOrdersPage } from './pages/dem/DemOrdersPage';
import { DemBlanketOrdersListPage } from './pages/dem/DemBlanketOrdersListPage';
import { DemBlanketOrdersNewPage } from './pages/dem/DemBlanketOrdersNewPage';
import { DemBlanketOrdersDetailPage } from './pages/dem/DemBlanketOrdersDetailPage';
import { DemDeliveriesListPage } from './pages/dem/DemDeliveriesListPage';
import { DemDeliveriesNewPage } from './pages/dem/DemDeliveriesNewPage';
import { DemDeliveriesDetailPage } from './pages/dem/DemDeliveriesDetailPage';
import { DemPlaceholderPage } from './pages/dem/DemPlaceholderPage';
import { DEM_DEFAULT_SLUG, DEM_MODULES } from './lib/demModules';
import { InventoryListPage } from './pages/inventory/InventoryListPage';
import { InventoryOpenPoPage } from './pages/inventory/InventoryOpenPoPage';
import { ReportsHomePage } from './pages/report/ReportsHomePage';
import { StorageBillingReportPage } from './pages/report/StorageBillingReportPage';
import { AccountsPortalApp } from './components/AccountsPortalApp';
import { AccountsCollectionPage } from './pages/accounts/AccountsCollectionPage';
import { ACCOUNTS_DEFAULT_SLUG } from './lib/accountsModules';
import { PurchasingItemsPage } from './pages/purchasing/PurchasingItemsPage';
import { DesignCataloguesListPage } from './pages/design/DesignCataloguesListPage';
import { DesignCatalogueEditorPage } from './pages/design/DesignCatalogueEditorPage';
import { PortalProvider } from './contexts/PortalContext';
import { RequireAuth } from './components/RequireAuth';
import { CrmDataProvider } from './contexts/CrmDataContext';
import { AppShell } from './components/layout/AppShell';
import { RequirePortalChoice } from './components/RequirePortalChoice';
import { DocumentTitle } from './components/DocumentTitle';
import { IS_UNIFIED_PORTAL, type PortalKind } from './lib/portal';
import { AdminHubPage } from './pages/AdminHubPage';
import { AdminHubShell } from './components/layout/AdminHubShell';
import { CalendarWorkspaceApp } from './components/CalendarWorkspaceApp';
import { CalendarHomePage } from './pages/calendar/CalendarHomePage';
import { CalendarTasksPage } from './pages/calendar/CalendarTasksPage';
import { FleetPage } from './pages/fleet/FleetPage';
import { FLEET_FEATURE_ENABLED } from './lib/fleetFeature';
import { FormsPortalApp } from './components/FormsPortalApp';
import { FormsHomePage } from './pages/forms/FormsHomePage';
import { AccountCreationDetailPage } from './pages/forms/AccountCreationDetailPage';
import { AccountCreationListPage } from './pages/forms/AccountCreationListPage';
import { AccountCreationNewPage } from './pages/forms/AccountCreationNewPage';
import { CustomerFeedbackDetailPage } from './pages/forms/CustomerFeedbackDetailPage';
import { CustomerFeedbackListPage } from './pages/forms/CustomerFeedbackListPage';
import { CustomerFeedbackNewPage } from './pages/forms/CustomerFeedbackNewPage';
import { InternalMemoDetailPage } from './pages/forms/InternalMemoDetailPage';
import { InternalMemoListPage } from './pages/forms/InternalMemoListPage';
import { InternalMemoNewPage } from './pages/forms/InternalMemoNewPage';
import { CertificateRequestDetailPage } from './pages/forms/CertificateRequestDetailPage';
import { CertificateRequestListPage } from './pages/forms/CertificateRequestListPage';
import { CertificateRequestNewPage } from './pages/forms/CertificateRequestNewPage';
import { CustomiseItemsTransferDetailPage } from './pages/forms/CustomiseItemsTransferDetailPage';
import { CustomiseItemsTransferListPage } from './pages/forms/CustomiseItemsTransferListPage';
import { CustomiseItemsTransferNewPage } from './pages/forms/CustomiseItemsTransferNewPage';
import { PricingUpdateDetailPage } from './pages/forms/PricingUpdateDetailPage';
import { PricingUpdateListPage } from './pages/forms/PricingUpdateListPage';
import { PricingUpdateNewPage } from './pages/forms/PricingUpdateNewPage';
import { FORMS_DEFAULT_SLUG } from './lib/formModules';
import { RootRedirectPage } from './pages/RootRedirectPage';

const portalRoutes = (
  <>
    <Route index element={<DashboardPage />} />
    <Route path="accounts" element={<AccountsPage />} />
    <Route path="accounts/:code" element={<AccountDetailPage />} />
    <Route path="contacts" element={<ContactsPage />} />
    <Route path="projects" element={<ProjectsPage />} />
    <Route path="projects/id/:id" element={<ProjectDetailPage />} />
    <Route path="projects/:ref" element={<ProjectDetailPage />} />
    <Route path="tasks" element={<TasksPage />} />
    <Route path="analytics" element={<AnalyticsPage />} />
    <Route
      path="fleet"
      element={FLEET_FEATURE_ENABLED ? <FleetPage /> : <Navigate to=".." replace />}
    />
    <Route path="catalogue" element={<CataloguePage />} />
    <Route path="data-gov-sg" element={<DataGovSgPage />} />
    <Route path="tickets" element={<TicketsPage />} />
    <Route path="tickets/:id" element={<TicketsPage />} />
    <Route path="approvals" element={<ApprovalsPage />} />
    <Route
      path="settings"
      element={IS_UNIFIED_PORTAL ? <Navigate to="/admin" replace /> : <SettingsPage />}
    />
  </>
);

const demRoutes = (
  <>
    <Route index element={<Navigate to={DEM_DEFAULT_SLUG} replace />} />
    <Route path="theme-preview" element={<DemThemePreviewPage />} />
    {DEM_MODULES.map((mod) => {
      if (mod.slug === 'customers') {
        return <Route key={mod.slug} path={mod.slug} element={<DemCustomersPage />} />;
      }
      if (mod.slug === 'standard-sales-lines') {
        return (
          <Route key={mod.slug} path={mod.slug} element={<DemStandardSalesLinesPage />} />
        );
      }
      if (mod.slug === 'items') {
        return <Route key={mod.slug} path={mod.slug} element={<DemItemsPage />} />;
      }
      if (mod.slug === 'sku-map') {
        return <Route key={mod.slug} path={mod.slug} element={<DemSkuMapPage />} />;
      }
      if (mod.slug === 'cloudtalk') {
        return <Route key={mod.slug} path={mod.slug} element={<DemCloudTalkPage />} />;
      }
      if (mod.slug === 'master-data') {
        return (
          <Route
            key={mod.slug}
            path={mod.slug}
            element={<Navigate to="/admin" replace />}
          />
        );
      }
      if (mod.slug === 'analytics') {
        return <Route key={mod.slug} path={mod.slug} element={<DemAnalyticsPage />} />;
      }
      if (mod.slug === 'dispatch') {
        return (
          <Route
            key={mod.slug}
            path={mod.slug}
            element={<Navigate to={DEM_DEFAULT_SLUG} replace />}
          />
        );
      }
      if (mod.slug === 'deliveries') {
        return (
          <>
            <Route key={`${mod.slug}-list`} path={mod.slug} element={<DemDeliveriesListPage />} />
            <Route key={`${mod.slug}-new`} path={`${mod.slug}/new`} element={<DemDeliveriesNewPage />} />
            <Route key={`${mod.slug}-detail`} path={`${mod.slug}/:id`} element={<DemDeliveriesDetailPage />} />
          </>
        );
      }
      if (mod.slug === 'customer-feedback') {
        return (
          <>
            <Route key={`${mod.slug}-list`} path={mod.slug} element={<CustomerFeedbackListPage />} />
            <Route key={`${mod.slug}-new`} path={`${mod.slug}/new`} element={<CustomerFeedbackNewPage />} />
            <Route
              key={`${mod.slug}-detail`}
              path={`${mod.slug}/:id`}
              element={<CustomerFeedbackDetailPage />}
            />
          </>
        );
      }
      if (mod.slug === 'pricing-update') {
        return (
          <>
            <Route key={`${mod.slug}-list`} path={mod.slug} element={<PricingUpdateListPage />} />
            <Route key={`${mod.slug}-new`} path={`${mod.slug}/new`} element={<PricingUpdateNewPage />} />
            <Route
              key={`${mod.slug}-detail`}
              path={`${mod.slug}/:id`}
              element={<PricingUpdateDetailPage />}
            />
          </>
        );
      }
      if (mod.slug === 'orders') {
        return <Route key={mod.slug} path={mod.slug} element={<DemOrdersPage />} />;
      }
      if (mod.slug === 'blanket-orders') {
        return (
          <>
            <Route
              key={`${mod.slug}-list`}
              path={mod.slug}
              element={<DemBlanketOrdersListPage />}
            />
            <Route
              key={`${mod.slug}-new`}
              path={`${mod.slug}/new`}
              element={<DemBlanketOrdersNewPage />}
            />
            <Route
              key={`${mod.slug}-detail`}
              path={`${mod.slug}/:id`}
              element={<DemBlanketOrdersDetailPage />}
            />
          </>
        );
      }
      return (
        <Route
          key={mod.slug}
          path={mod.slug}
          element={
            <DemPlaceholderPage
              title={mod.title}
              description={mod.description}
              desktopModule={mod.desktopModule}
            />
          }
        />
      );
    })}
    {/* Legacy channel tabs → unified Orders */}
    <Route path="po-orders" element={<Navigate to="/dem/orders" replace />} />
    <Route path="web-orders" element={<Navigate to="/dem/orders" replace />} />
    <Route path="whatsapp" element={<Navigate to="/dem/orders" replace />} />
    <Route path="call-in-orders" element={<Navigate to="/dem/orders" replace />} />
  </>
);

const formsRoutes = (
  <>
    <Route index element={<FormsHomePage />} />
    <Route path="account-creation/new" element={<AccountCreationNewPage />} />
    <Route path="account-creation/:id" element={<AccountCreationDetailPage />} />
    <Route path="account-creation" element={<AccountCreationListPage />} />
    <Route path="customer-feedback/new" element={<CustomerFeedbackNewPage />} />
    <Route path="customer-feedback/:id" element={<CustomerFeedbackDetailPage />} />
    <Route path="customer-feedback" element={<CustomerFeedbackListPage />} />
    <Route path="internal-memo/new" element={<InternalMemoNewPage />} />
    <Route path="internal-memo/:id" element={<InternalMemoDetailPage />} />
    <Route path="internal-memo" element={<InternalMemoListPage />} />
    <Route path="certificate-request/new" element={<CertificateRequestNewPage />} />
    <Route path="certificate-request/:id" element={<CertificateRequestDetailPage />} />
    <Route path="certificate-request" element={<CertificateRequestListPage />} />
    <Route path="customise-items-transfer/new" element={<CustomiseItemsTransferNewPage />} />
    <Route path="customise-items-transfer/:id" element={<CustomiseItemsTransferDetailPage />} />
    <Route path="customise-items-transfer" element={<CustomiseItemsTransferListPage />} />
    <Route path="pricing-update/new" element={<PricingUpdateNewPage />} />
    <Route path="pricing-update/:id" element={<PricingUpdateDetailPage />} />
    <Route path="pricing-update" element={<PricingUpdateListPage />} />
    <Route path={FORMS_DEFAULT_SLUG} element={<Navigate to="account-creation" replace />} />
  </>
);

const reportRoutes = (
  <>
    <Route index element={<ReportsHomePage />} />
    <Route path="item-analysis" element={<InventoryListPage />} />
    <Route path="open-po" element={<InventoryOpenPoPage />} />
    <Route path="storage-billing" element={<StorageBillingReportPage />} />
  </>
);

const accountsRoutes = (
  <>
    <Route index element={<Navigate to={ACCOUNTS_DEFAULT_SLUG} replace />} />
    <Route path="collection" element={<AccountsCollectionPage />} />
    <Route path="collection/:motherCode" element={<AccountsCollectionPage />} />
  </>
);

const purchasingRoutes = (
  <>
    <Route index element={<Navigate to="items" replace />} />
    <Route path="items" element={<PurchasingItemsPage />} />
  </>
);

const designRoutes = (
  <>
    <Route index element={<Navigate to="catalogues" replace />} />
    <Route path="catalogues" element={<DesignCataloguesListPage />} />
    <Route path="catalogues/new" element={<DesignCatalogueEditorPage />} />
    <Route path="catalogues/:id" element={<DesignCatalogueEditorPage />} />
  </>
);

function legacyPortalKind(): PortalKind {
  return (import.meta.env.VITE_PORTAL_KIND ?? 'crm').trim() === 'it' ? 'it' : 'crm';
}

function LegacyAppRoutes() {
  return (
    <Route
      element={
        <PortalProvider kind={legacyPortalKind()}>
          <RequireAuth>
          <RequirePortalChoice>
            <CrmDataProvider>
              <AppShell />
            </CrmDataProvider>
          </RequirePortalChoice>
        </RequireAuth>
        </PortalProvider>
      }
    >
      {portalRoutes}
    </Route>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <DocumentTitle />
        <ScrollToTopOnNavigate />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/not-authorized" element={<NotAuthorizedPage />} />
          <Route
            path="/choose-portal"
            element={
              <RequireAuth>
                <PortalChoosePage />
              </RequireAuth>
            }
          />

          {IS_UNIFIED_PORTAL ? (
            <>
              <Route path="/crm" element={<PortalApp kind="crm" />}>
                {portalRoutes}
              </Route>
              <Route path="/it" element={<PortalApp kind="it" />}>
                {portalRoutes}
              </Route>
              <Route path="/dem" element={<DemPortalApp kind="dem" />}>
                {demRoutes}
              </Route>
              <Route path="/forms" element={<FormsPortalApp kind="forms" />}>
                {formsRoutes}
              </Route>
              <Route path="/applicationform" element={<Navigate to="/forms" replace />} />
              <Route path="/applicationform/*" element={<Navigate to="/forms" replace />} />
              <Route path="/applicationforms" element={<Navigate to="/forms" replace />} />
              <Route path="/application-form" element={<Navigate to="/forms" replace />} />
              <Route path="/inventory" element={<Navigate to="/report/item-analysis" replace />} />
              <Route path="/inventory/list" element={<Navigate to="/report/item-analysis" replace />} />
              <Route path="/inventory/open-po" element={<Navigate to="/report/open-po" replace />} />
              <Route path="/report" element={<ReportPortalApp kind="report" />}>
                {reportRoutes}
              </Route>
              <Route path="/accounts" element={<AccountsPortalApp kind="accounts" />}>
                {accountsRoutes}
              </Route>
              <Route path="/purchasing" element={<PurchasingPortalApp kind="purchasing" />}>
                {purchasingRoutes}
              </Route>
              <Route path="/design" element={<DesignPortalApp kind="design" />}>
                {designRoutes}
              </Route>
              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <AdminHubShell />
                  </RequireAuth>
                }
              >
                <Route index element={<AdminHubPage />} />
              </Route>
              <Route path="/calendar/:slug" element={<CalendarWorkspaceApp />}>
                <Route index element={<CalendarHomePage />} />
                <Route path="tasks" element={<CalendarTasksPage />} />
              </Route>
              <Route path="/calendar" element={<Navigate to="/calendar/jason_chong" replace />} />
              <Route path="/" element={<RootRedirectPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <>
              {LegacyAppRoutes()}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
